import { useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { CanceledError } from "axios";
import { useErrorBoundary } from "react-error-boundary";
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from "@shared/lib/utils";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { Work, WorkSearch } from "@models/work";
import { buildWorkSort, searchWorks, WorkSearchParams } from "../../../api/works.service";
import { useEntityBrowse } from "../../../hooks/useEntityBrowse";
import { DEFAULT_SORT_KEY, parseWorksUrlParams, syncWorksUrlParams } from "./worksUrlParams";

export function useWorksBrowse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBoundary } = useErrorBoundary();

  const urlParams = useMemo(() => parseWorksUrlParams(searchParams), [searchParams]);

  const [data, setData] = useState<Work[]>([]);
  const [sortKey, setSortKey] = useState(urlParams.sortKey || DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(urlParams.sortMethod);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const workSearchForm = useForm<WorkSearch>({
    defaultValues: urlParams.filters ?? {},
  });

  async function getData(searchData: WorkSearch): Promise<void> {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    try {
      const sort = buildWorkSort(sortKey, sortMethod);
      const payload: WorkSearchParams = {
        filter: deleteEmptyFieldsFromObject(searchData),
        sort: [sort],
      };
      const works = await searchWorks(payload, abortControllerRef.current.signal);
      setData(works.data);
      setIsLoading(false);
    } catch (error: unknown) {
      if (error instanceof CanceledError) {
        return;
      }
      handleErrorWithBoundary(error, showBoundary);
      setIsLoading(false);
    }
  }

  const onSearch: SubmitHandler<WorkSearch> = (formData) => {
    syncWorksUrlParams(setSearchParams, formData, sortKey, sortMethod);
    getData(formData);
  };

  useEntityBrowse({
    form: workSearchForm,
    sortKey,
    sortMethod,
    onSearch,
  });

  function handleSortKeyChanged(value: string) {
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function reset() {
    const values = setAllValuesToNull(workSearchForm.getValues());

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        workSearchForm.setValue(key as keyof WorkSearch, values[key as keyof WorkSearch]);
      }
    }
  }

  return {
    workSearchForm,
    data,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    reset,
  };
}
